/**
 * The ONE place a Dispatch log row becomes what a rep reads (plan
 * 2026-09-27-003, U3). DOM-free and fetch-free, so it runs under `bun:test`
 * and the inspector only renders what this returns.
 *
 * # One status per person
 *
 * The old row showed TWO badges — the transport axis (`result`: Sent/Failed)
 * and the Clay axis (`result_status`: Clay done / In Clay / No answer / Clay
 * error) — plus Clay's raw words. A rep had to know that "Sent + Clay error"
 * on an Invalid Email meant "Clay correctly didn't send". Now each row gets
 * exactly one of five statuses:
 *
 *   delivered        Gong / Inflection accepted the person.
 *   in_progress      Clay is still working (or hasn't confirmed yet).
 *   not_sent         Someone — Terra, a gate, or Clay itself — decided on
 *                    purpose not to send. Grey; nothing broke.
 *   failed_to_start  The send never reached Clay; Retry is safe.
 *   crashed          A Clay step errored mid-run; ops may be alerted.
 *
 * # Priority order (first match wins)
 *
 *   1. `result='failed'` + a skip prefix       → not_sent (reworded)
 *   2. `result='failed'` otherwise             → failed_to_start
 *   3. `result` queued / processing            → in_progress
 *   4. `result='sent'`, no Clay status at all  → in_progress while young, then
 *                                                delivered ("not tracked")
 *   5. `clay_pending` / `clay_running`         → in_progress (by channel)
 *   6. `clay_not_found`                        → in_progress, needs a recheck
 *   7a. terminal `clay_*`, unreadable data     → by `result_status` alone:
 *                                                failed → crashed (ops NOT
 *                                                claimed alerted), blocked →
 *                                                not_sent, completed → delivered
 *                                                ("not tracked")
 *   7. terminal `clay_*`                       → by Clay's OWN status WORD first
 *
 * Row 7 reads `clay_run.status` before `result_status` so a row already stored
 * under the old mapping (`clay_failed` + `Invalid Email`, from before the
 * projection fix) renders correctly with no data migration.
 *
 * Inside row 7, a REFUSAL is separated from a CRASH by the node V3 stamped on
 * the block: `Compute final status` is Clay deciding not to send (a reason only
 * a rep or an admin can fix) → not_sent, "Clay stopped the send"; `Clay routine
 * run` — and any `Error` whose reason cleans away to nothing, or cleans to
 * something with no words in it or to a bare status code — is a step that
 * actually threw → crashed. Also no migration: both are already in stored data.
 * See `docs/playbooks-sharing-rules.md` edge cases (l) and (m). [R16][R20]
 *
 * A refusal from the `Stop unsafe delivery` gate also sets `retryable`, and its
 * banner then tells the rep what Retry does (rebuilds from the play's current
 * copy and sender). The eligibility test itself lives with the reason table in
 * `dispatch-inspector-logic.ts#isProvenSafeRefusal` — shared with the server,
 * so the button and the server's arm cannot disagree. (m)
 *
 * "Nothing went out" is a CLAIM, and only that gate proves it (n). Every other
 * final-status stop, and every crash, hedges: "Terra can't confirm whether
 * anything reached <First> — check in <channel> before resending." Terra will
 * be able to say more only once Clay reports `failure.sent` on a failed run.
 *
 * Never returns a `clay_*` word, an HTTP status, or a `[launcher.dispatch]`
 * tag in `pillText` / `result` — pinned by this module's test.
 */
import {
  CLAY_FINAL_STATUS_NON_SEND,
  CLAY_RUN_STATUS_FINISHED_UNTRACKED,
  readClayRunResultData,
  readDispatchReconcile,
  type ClayCheck,
  type ClayCheckState,
  type ClayRunResultData,
  type RepDispatchStatus,
} from '../_shared/clay-run-status-schema';
import {
  CLAY_STATUS_WAIT_MS,
  DISPATCH_RECONCILING_CAPTION,
  DISPATCH_START_FAILURE_BASE,
  FINAL_STATUS_BLOCK_NODE,
  classifyDispatchResult,
  dispatchFailureSummary,
  dispatchStartFailureSummary,
  isCopyDefectError,
  isMemberRemovedError,
  isRetryableDispatchRow,
  retryArmOf,
  isSenderMissingError,
  isSkippedMemberError,
  isStopUnsafeDeliveryReason,
  isClayBusyStartFailure,
  isTransportCrash,
  readAutoRetries,
  readCrashTriage,
  skippedMemberSummary,
  TRANSPORT_CRASH_AUTO_RETRY_MAX,
} from './dispatch-inspector-logic';
import {
  describeBlockReason,
  describeClayFailure,
  plainBlockReason,
  type ClayFailureView,
} from './dispatch-block-reasons';
import { isRecheckEligible, rowStatusAnchor } from './dispatch-recheck-eligibility';
import type { PlaybooksDispatchLogRow } from './schemas';

export type { RepDispatchStatus };

// ── Display tables ───────────────────────────────────────────────────────────

/** Order of the summary badges, filter chips and the summary line. */
export const REP_STATUS_ORDER: readonly RepDispatchStatus[] = [
  'delivered',
  'in_progress',
  'not_sent',
  'failed_to_start',
  'crashed',
];

/** The filter-chip label per status (the approved prototype's words). */
export const REP_STATUS_CHIP_LABEL: Record<RepDispatchStatus, string> = {
  delivered: 'Delivered',
  in_progress: 'In progress',
  not_sent: 'Not sent',
  failed_to_start: 'Failed to start',
  crashed: 'Crashed in Clay',
};

/** Glaze `StatusBadge` status per rep status. Not sent is NEUTRAL — nothing broke. */
export const REP_STATUS_BADGE: Record<
  RepDispatchStatus,
  'neutral' | 'action' | 'success' | 'warning' | 'error'
> = {
  delivered: 'success',
  in_progress: 'action',
  not_sent: 'neutral',
  failed_to_start: 'error',
  crashed: 'error',
};

/** The count half of a summary badge: `10 delivered`, `2 not sent`, … */
const SUMMARY_WORD: Record<RepDispatchStatus, string> = {
  delivered: 'delivered',
  in_progress: 'in progress',
  not_sent: 'not sent',
  failed_to_start: 'failed to start',
  crashed: 'crashed in Clay',
};

export function repStatusCountLabel(status: RepDispatchStatus, count: number): string {
  return `${String(count)} ${SUMMARY_WORD[status]}`;
}

/**
 * Typical time for a Clay run to finish, by channel — the pill says it so a
 * rep knows whether to wait. Measured over 50 V3 runs since 2026-09-01: p50
 * 72s overall (Gong runs cluster there, hence ~2 min); every run over 5 min
 * was an Inflection send, p90 712s / max 769s (hence ~10 min).
 */
const TYPICAL_MINUTES_BY_CHANNEL: Readonly<Record<string, number>> = {
  gong_engage: 2,
  inflection: 10,
};

/** Short rep-facing channel names (the prototype's "Gong" / "Inflection"). */
const CHANNEL_SHORT_LABEL: Readonly<Record<string, string>> = {
  gong_engage: 'Gong',
  inflection: 'Inflection',
  slack_copilot: 'Terra',
};

/** `Gong` / `Inflection` / `Terra`, or `null` for a channel this UI doesn't know (never guessed). */
export function repChannelLabel(channel: string | null): string | null {
  if (channel === null) return null;
  return CHANNEL_SHORT_LABEL[channel] ?? null;
}

// ── The view ─────────────────────────────────────────────────────────────────

/** A checklist row's state. `wait` is the in-progress icon (prototype) — not a V3 state. */
export type RepCheckState = ClayCheckState | 'wait';

export interface RepCheckView {
  state: RepCheckState;
  reason: string;
}

export interface RepStatusView {
  status: RepDispatchStatus;
  /** The one pill in the row's Status column. */
  pillText: string;
  /** The banner above "What was sent", or `null` for none. */
  banner: { tone: 'grey' | 'red' | 'blue'; text: string } | null;
  /** The "Result:" line under "What Clay did". */
  result: string;
  /** `Gong` / `Inflection` / `Terra`, or `null` when the row's channel is unknown. */
  channelLabel: string | null;
  /**
   * Offer Retry — the server's own eligibility rule, never a second reading:
   * `retryArmOf` (`dispatch-inspector-logic.ts`) — a failed START, a proven-safe
   * refusal (m), a network crash (o), or, since PR 1, ANY other Clay-failed row
   * on the general arm (u).
   */
  retryable: boolean;
  /**
   * When Retry must ask first, the sentence the confirm shows; `null` = Retry
   * acts on the click. Set for every retry that resends a stored job Terra
   * cannot prove undelivered — a network crash (o) and any other Clay failure
   * (u) — never for a test send.
   */
  retryConfirm: string | null;
  /** V3 (`clay_alert_posted`) or Terra (`clay_crash_alerted`) told ops about a crash. */
  opsAlerted: boolean;
  /** In progress for longer than the poller watches — the inspector re-asks Clay. */
  needsRecheck: boolean;
  /** Clay's routine run id, for the tertiary "run id" text. */
  routineRunId: string | null;
  /** Clay's workflow run id (a reconciled / retried row), when that is all Terra has. */
  workflowRunId: string | null;
  checks: {
    qualification: RepCheckView;
    emailValidation: RepCheckView;
    copyQa: RepCheckView;
    delivery: RepCheckView;
  };
}

const NOT_REACHED: RepCheckView = { state: 'skip', reason: "Didn't get this far" };
/**
 * A crash's two unknown rows. Distinct from `NOT_REACHED`: when a step inside
 * Clay errored, Terra has no result for these checks — which is NOT the same
 * claim as knowing they never ran. A Terra-side skip and a send that never
 * reached Clay keep `NOT_REACHED`, where Terra really does know. [R16], (l).
 */
const NOT_REPORTED: RepCheckView = { state: 'skip', reason: 'Not reported by Clay' };
const PASSED: RepCheckView = { state: 'pass', reason: 'Passed' };

function firstName(row: Pick<PlaybooksDispatchLogRow, 'contact_name'>): string | null {
  const first = (row.contact_name ?? '').trim().split(/[\s,]+/)[0];
  return first === undefined || first === '' ? null : first;
}

function qualificationCheck(row: Pick<PlaybooksDispatchLogRow, 'bucket'>): RepCheckView {
  return {
    state: 'pass',
    reason:
      row.bucket === null || row.bucket.trim() === ''
        ? "Matched the play's audience"
        : `Matched segment ${row.bucket}`,
  };
}

function isRecent(timestamp: string | null, nowMs: number, windowMs: number): boolean {
  const at = timestamp === null ? Number.NaN : Date.parse(timestamp);
  if (Number.isNaN(at)) return false;
  return nowMs - at < windowMs;
}

function fromClay(check: ClayCheck | undefined, fallback: RepCheckView): RepCheckView {
  return check === undefined ? fallback : { state: check.state, reason: check.reason };
}

interface Ctx {
  row: PlaybooksDispatchLogRow;
  clay: ClayRunResultData | null;
  channelLabel: string | null;
  qualification: RepCheckView;
}

type ViewFields = Omit<
  RepStatusView,
  'channelLabel' | 'routineRunId' | 'workflowRunId' | 'opsAlerted' | 'needsRecheck' | 'retryable' | 'retryConfirm'
> &
  Partial<Pick<RepStatusView, 'opsAlerted' | 'needsRecheck' | 'retryable' | 'retryConfirm'>>;

function view(ctx: Ctx, fields: ViewFields): RepStatusView {
  return {
    retryable: false,
    retryConfirm: null,
    opsAlerted: false,
    needsRecheck: false,
    ...fields,
    channelLabel: ctx.channelLabel,
    routineRunId: ctx.clay?.clay_run.routine_run_id ?? null,
    workflowRunId: ctx.clay?.clay_run.workflow_run_id ?? null,
  };
}

/** Clay-side checks for a row: V3's own block when it sent one, else derived only as far as the data allows. */
function clayChecks(
  ctx: Ctx,
  derived: { emailValidation: RepCheckView; copyQa: RepCheckView; delivery: RepCheckView },
): RepStatusView['checks'] {
  const v3 = ctx.clay?.clay_checks;
  return {
    qualification: ctx.qualification,
    emailValidation: fromClay(v3?.email_validation, derived.emailValidation),
    copyQa: fromClay(v3?.copy_qa, derived.copyQa),
    delivery: fromClay(v3?.delivery, derived.delivery),
  };
}

function inProgress(
  ctx: Ctx,
  pillText: string,
  opts: { needsRecheck?: boolean; bannerText: string },
): RepStatusView {
  const waitingFor = ctx.channelLabel === null ? 'Waiting for Clay' : `Waiting for ${ctx.channelLabel}`;
  return view(ctx, {
    status: 'in_progress',
    pillText,
    needsRecheck: opts.needsRecheck ?? false,
    banner: { tone: 'blue', text: opts.bannerText },
    result: pillText,
    checks: clayChecks(ctx, {
      emailValidation: { state: 'wait', reason: 'Waiting for Clay' },
      copyQa: { state: 'wait', reason: 'Waiting for Clay' },
      delivery: { state: 'wait', reason: waitingFor },
    }),
  });
}

const STALE_PILL = 'Waiting on Clay to confirm · taking longer than usual';
const STALE_BANNER =
  "Clay hasn't confirmed this one yet. Terra checks again each time you open the play — nothing to do for now.";

function sending(ctx: Ctx): { pill: string; banner: string } {
  const minutes = ctx.row.channel === null ? undefined : TYPICAL_MINUTES_BY_CHANNEL[ctx.row.channel];
  if (ctx.channelLabel === null || minutes === undefined) {
    return { pill: 'Sending through Clay', banner: 'Clay is sending this now.' };
  }
  return {
    pill: `Sending through ${ctx.channelLabel} · usually ~${String(minutes)} min`,
    banner: `Clay is sending this now. Give it about ${String(minutes)} minutes.`,
  };
}

type NonSendWord = (typeof CLAY_FINAL_STATUS_NON_SEND)[number];

/** Clay's own words for a deliberate non-send → the rep's words. */
const NOT_SENT_PILL: Readonly<Record<NonSendWord, string>> = {
  'Invalid Email': "Email isn't deliverable",
  Suppressed: 'Contact is suppressed',
  'Already in Flow': 'Already in this Gong flow',
  Skipped: 'Marked as skipped',
};

const NOT_SENT_RESULT: Readonly<Record<NonSendWord, string>> = {
  'Invalid Email': "Email isn't deliverable, so Clay didn't send",
  Suppressed: "This contact is suppressed, so Clay didn't send",
  'Already in Flow': "They're already in this Gong flow, so Clay didn't add them again",
  Skipped: "Marked as skipped, so Clay didn't send",
};

function isNonSendWord(word: string): word is NonSendWord {
  return (CLAY_FINAL_STATUS_NON_SEND as readonly string[]).includes(word);
}

function notSentBanner(ctx: Ctx): { tone: 'grey'; text: string } {
  const name = firstName(ctx.row);
  return {
    tone: 'grey',
    text: `Nothing went out${name === null ? '' : ` to ${name}`}. This is the copy that was prepared.`,
  };
}

function delivered(ctx: Ctx, pill: string): RepStatusView {
  return view(ctx, {
    status: 'delivered',
    pillText: pill,
    banner: null,
    result: pill,
    checks: clayChecks(ctx, { emailValidation: PASSED, copyQa: PASSED, delivery: PASSED }),
  });
}

/** Sent, and Terra can't say what Clay delivered: "Handed to Clay · delivery not tracked". */
function notTracked(ctx: Ctx): RepStatusView {
  const skipped = { state: 'skip' as const, reason: 'Not tracked for this send' };
  return view(ctx, {
    status: 'delivered',
    pillText: 'Handed to Clay · delivery not tracked for this send',
    banner: null,
    result: 'Clay accepted this send. Terra did not track delivery for it.',
    checks: {
      qualification: ctx.qualification,
      emailValidation: skipped,
      copyQa: skipped,
      delivery: skipped,
    },
  });
}

/**
 * "Terra can't confirm whether anything reached <who> — <where> before
 * resending." — the one hedge (n), shared by an unproven stop and a crash.
 *
 * Terra owns the whole sentence, so it never names a channel it does not
 * recognise: an unknown `row.channel` becomes "check with Clay", which is
 * where a rep can actually look. No first name becomes "this person".
 */
function cannotConfirmDelivery(ctx: Ctx): string {
  const who = firstName(ctx.row) ?? 'this person';
  const where = ctx.channelLabel === null ? 'check with Clay' : `check in ${ctx.channelLabel}`;
  return `Terra can't confirm whether anything reached ${who} — ${where} before resending.`;
}

/**
 * Clay's own refusal banner (edge case (l)) — parallel to `notSentBanner`, same
 * `{ to <First> }` conditional, but it names Clay's reason and what to do. Never
 * "ops alerted": nobody was alerted, and the rep is the one who can fix it. [R20]
 *
 * Three shapes, and the split is about what Terra can PROVE (n):
 *
 *   PROVEN SAFE, retryable  `Stop unsafe delivery` sits before the node that
 *                           picks a delivery channel, so "Nothing went out" is
 *                           a fact about the shape of the V3 run. The rep is
 *                           told what Retry does — send the play as it is now.
 *   PROVEN SAFE, not        Same proof, no button: the original "resend".
 *                           (A refusal reason on a row Terra never marked
 *                           `sent`, so `isProvenSafeRefusal` is false.)
 *   NOT PROVEN              Any other final-status stop — `Gong returned no
 *                           enrollment id` is the live one. It is non-retryable
 *                           PRECISELY because delivery may already have
 *                           happened, so opening "Nothing went out" and
 *                           closing "resend" told the rep to duplicate an
 *                           email Clay may already have sent. Hedge instead.
 */
function clayRefusalBanner(
  ctx: Ctx,
  why: string,
  retryable: boolean,
  provenSafe: boolean,
): { tone: 'grey'; text: string } {
  const name = firstName(ctx.row);
  const reason = why.replace(/\.\s*$/, '');
  if (!provenSafe) {
    return { tone: 'grey', text: `Clay stopped this send: ${reason}. ${cannotConfirmDelivery(ctx)}` };
  }
  const next = retryable
    ? 'Fix the copy (or the sender) in the play, then Retry — Terra sends the current version.'
    : 'Fix it in the play and resend.';
  return {
    tone: 'grey',
    text: `Nothing went out${name === null ? '' : ` to ${name}`}. Clay stopped this send: ${reason}. ${next}`,
  };
}

/**
 * Clay's own refusal shape: which checklist row carries the stop. A copy-QA
 * refusal never reached delivery; every other refusal (sender, copy missing,
 * a gate) cleared email validation and stopped at delivery. [R16]
 */
interface ClayRefusal {
  copyQa: boolean;
  findings: string | null;
}

/**
 * Clay refused the send (a gate, not a crash). `why` is already plain.
 *
 * `retryable` is the SERVER's own eligibility rule (`isProvenSafeRefusal`, in
 * `dispatch-inspector-logic.ts`), not a second reading of the row — so the
 * button the UI offers here and the arm `dispatch-retry.ts` takes can never
 * disagree. It is false for every legacy `clay_blocked` row: only a refusal
 * Clay proved it made before delivery is retryable (m).
 *
 * `provenSafe` is the separate question the BANNER turns on (n): may Terra
 * assert "Nothing went out"? It reads only the reason, because the other three
 * facts `isProvenSafeRefusal` checks are what put the row on this path in the
 * first place — and a row that got here with a proven-safe reason but which
 * Terra never marked `sent` should still not be told delivery may have
 * happened. Defaults to `true` for the callers that reach `clayStopped` with
 * no `refusal` at all: a legacy `clay_blocked` row and the four non-send words
 * are pre-delivery by construction (`routine-result-projection.ts`), and they
 * get `notSentBanner` anyway.
 */
function clayStopped(ctx: Ctx, why: string, refusal?: ClayRefusal, provenSafe = true): RepStatusView {
  const arm = retryArmOf(ctx.row);
  const retryable = arm !== null;
  const derived =
    refusal === undefined
      ? { emailValidation: PASSED, copyQa: PASSED, delivery: { state: 'stop' as const, reason: why } }
      : refusal.copyQa
        ? { emailValidation: PASSED, copyQa: { state: 'stop' as const, reason: refusal.findings ?? why }, delivery: NOT_REACHED }
        : { emailValidation: PASSED, copyQa: NOT_REACHED, delivery: { state: 'stop' as const, reason: why } };
  return view(ctx, {
    status: 'not_sent',
    pillText: 'Clay stopped the send',
    retryable,
    // An unproven stop (`Gong returned no enrollment id`, …) is the general arm:
    // the same stored job, behind the rep's confirm.
    retryConfirm: arm === 'clay_failure' ? clayFailureConfirmFor(ctx) : null,
    banner: refusal === undefined ? notSentBanner(ctx) : clayRefusalBanner(ctx, why, retryable, provenSafe),
    result: `Clay stopped the send: ${why}`,
    checks: clayChecks(ctx, derived),
  });
}

/**
 * A step inside Clay errored. `opsAlerted` only when Terra can SEE an alert
 * went out.
 *
 * This banner used to open "Nothing went out", which Terra cannot prove for a
 * crash any more than it can for an unproven stop (n, Spencer's decision 5):
 * a `Clay routine run` crash is a step that THREW, and a Gong enroll node that
 * throws may well have enrolled the person first. Same hedge as the stop, with
 * the ops-alerted reassurance kept — what was removed is the false guarantee,
 * not the "you don't need to do anything". Terra will be able to state
 * non-delivery here only once Clay reports a `failure.sent` field on a failed
 * run (`plans/2026-09-30-001`, PR 5/6).
 */
function crashed(ctx: Ctx, opsAlerted: boolean): RepStatusView {
  const pill = opsAlerted ? 'Something broke in Clay — ops alerted' : 'Something broke in Clay';
  const who = firstName(ctx.row) ?? 'this person';
  const failure = describeClayFailure(ctx.clay?.clay_block?.reason);
  // What the rep reads first: Clay's own (redacted) reason when it gave one.
  const opening = failure.reason === null ? 'A step inside Clay errored.' : `A step inside Clay errored: ${stripTrailingStop(failure.reason)}.`;
  const hedge =
    failure.phase === 'after_delivery'
      ? `${capitalize(who)} may already have received this — ${ctx.channelLabel === null ? 'check with Clay' : `check in ${ctx.channelLabel}`} before you retry.`
      : ctx.channelLabel === null || opsAlerted
        ? `Terra can't confirm whether anything reached ${who}.`
        : cannotConfirmDelivery(ctx);
  const arm = retryArmOf(ctx.row);
  return view(ctx, {
    status: 'crashed',
    pillText: pill,
    opsAlerted,
    retryable: arm !== null,
    retryConfirm: arm === 'clay_failure' ? clayFailureConfirmFor(ctx) : null,
    banner: {
      tone: 'red',
      text: opsAlerted
        ? `${opening} ${hedge} Ops has been alerted and will follow up — you don't need to do anything.`
        : `${opening} ${hedge}`,
    },
    result: pill,
    checks: clayChecks(ctx, {
      emailValidation: NOT_REPORTED,
      copyQa: NOT_REPORTED,
      delivery: { state: 'error', reason: 'A step inside Clay errored' },
    }),
  });
}

function stripTrailingStop(text: string): string {
  return text.replace(/[.\s]+$/, '');
}

/**
 * The confirm a rep accepts before Retry resends the SAME stored job of a Clay
 * failure Terra cannot prove undelivered (sharing rules (u)) — `null` for a
 * test send, which never delivers. Reason and phase come from the row's own
 * stored `clay_block`, so the dialog and the banner can never disagree.
 */
function clayFailureConfirmFor(ctx: Ctx): string | null {
  if (ctx.row.is_test === true) return null;
  return clayFailureRetryConfirm(describeClayFailure(ctx.clay?.clay_block?.reason), firstName(ctx.row));
}

/**
 * The confirm sentence (authored in `docs/playbooks-sharing-rules.md` edge case
 * (u)). Names Clay's reason (or that it gave none) and, when Terra can place the
 * failure, which side of the message step it fell on:
 *
 *   before   "…happened before the message step…" — Terra's tables place it
 *            ahead of delivery (a function that refused to run);
 *   delivery "Clay errored at the message step…" — the delivery step itself;
 *   after    "…<First> may already have received this; retrying sends it again."
 *   unknown  the plain hedge — Terra cannot say.
 *
 * Never a status code, id or `clay_*` word: `describeClayFailure` redacts them.
 */
export function clayFailureRetryConfirm(failure: ClayFailureView, firstNameOrNull: string | null): string {
  const who = firstNameOrNull ?? 'this person';
  const at = failure.step === null ? '' : ` at "${failure.step}"`;
  const reason = failure.reason === null ? null : stripTrailingStop(failure.reason);
  const because = (opening: string) => (reason === null ? `${opening} and didn't say why.` : `${opening}: ${reason}.`);
  switch (failure.phase) {
    case 'before_delivery':
      return `${because(`Clay's error happened before the message step${at}`)} Retrying sends the same message again.`;
    case 'delivery':
      return `${because(`Clay errored at the message step${failure.step === null ? '' : ` ("${failure.step}")`}`)} Terra can't confirm whether anything reached ${who}; retrying sends the same message again.`;
    case 'after_delivery':
      return `${because(`Clay's error happened after the message step${at}`)} ${capitalize(who)} may already have received this; retrying sends it again.`;
    case 'unknown':
      return `${because(`Clay errored${at}`)} Terra can't confirm whether anything reached ${who}; retrying sends the same message again.`;
  }
}

/**
 * The confirm a rep must accept before retrying a run Clay broke on a network
 * error, when Terra cannot prove nothing went out — authored verbatim in
 * `docs/playbooks-sharing-rules.md` edge case (o). [R20]
 */
/**
 * The risk half of "Retry all"'s confirm when any of its rows resends a stored
 * job Terra cannot prove undelivered — a network crash (o) or any other Clay
 * failure (u). Written for a COUNT ("N of them errored inside Clay. …"), so it
 * names no person and no step.
 */
export const CLAY_FAILURE_RETRY_RISK =
  "Terra can't confirm whether those messages already went out, so retrying them could send twice.";

/** The risk half of {@link TRANSPORT_CRASH_RETRY_CONFIRM} — the per-row network-crash confirm (o). */
export const TRANSPORT_CRASH_RETRY_RISK =
  "Terra can't tell which step broke; if it was the delivery step, retrying could send twice.";

export const TRANSPORT_CRASH_RETRY_CONFIRM = `Clay broke on a network error before finishing. ${TRANSPORT_CRASH_RETRY_RISK}`;

const NETWORK_CRASH_OPENING = 'Clay broke on a network error before finishing.';

/**
 * A run Clay broke on a NETWORK error (o) — `isTransportCrash`. Unlike any
 * other crash it is RETRYABLE: the stored message was fine, the connection
 * was not. What the banner says depends on what Terra could prove:
 *
 *   test send    A test never delivers (V3 routes it to its preview before
 *                any delivery step), so Terra retries it on its own — no
 *                confirm on the manual Retry either.
 *   Gong         Terra asks Gong whether the person is in the flow before
 *                retrying on its own (`dispatch-crash-triage.ts`); when Gong
 *                couldn't be asked, the banner says so.
 *   otherwise    Terra can't tell which step broke: check the channel, and
 *                Retry asks for {@link TRANSPORT_CRASH_RETRY_CONFIRM} first.
 *
 * Never the error word itself (`ECONNRESET`, …) — R20.
 */
function transportCrashed(ctx: Ctx, opsAlerted: boolean): RepStatusView {
  const isTest = ctx.row.is_test === true;
  const triage = readCrashTriage(ctx.row.result_data);
  const capped =
    triage?.outcome === 'cap_reached' || readAutoRetries(ctx.row.result_data).length >= TRANSPORT_CRASH_AUTO_RETRY_MAX;
  const who = firstName(ctx.row) ?? 'this person';
  const where = ctx.channelLabel === null ? 'check with Clay' : `check in ${ctx.channelLabel}`;
  const times = `${String(TRANSPORT_CRASH_AUTO_RETRY_MAX)} times`;

  let text: string;
  if (isTest) {
    text = capped
      ? `Test run — ${NETWORK_CRASH_OPENING} Terra retried it ${times}; Retry tries again.`
      : `Test run — ${NETWORK_CRASH_OPENING} A test never sends anything, so Terra retries it on its own.`;
  } else if (triage?.outcome === 'gong_unavailable') {
    text = `${NETWORK_CRASH_OPENING} Terra couldn't reach Gong to check whether ${who} was added, so check in Gong before you retry.`;
  } else if (capped) {
    text = `Clay broke on a network error again after Terra retried it ${times}. ${capitalize(where)} before you retry ${who}.`;
  } else if (triage?.outcome === 'checking' && ctx.row.channel === 'gong_engage') {
    text = `${NETWORK_CRASH_OPENING} Terra is checking Gong and retries on its own if ${who} isn't in the flow.`;
  } else {
    text = `${NETWORK_CRASH_OPENING} Terra can't tell which step broke, so ${where} before you retry ${who}.`;
  }

  return view(ctx, {
    status: 'crashed',
    pillText: 'Clay broke on a network error',
    opsAlerted,
    retryable: true,
    retryConfirm: isTest ? null : TRANSPORT_CRASH_RETRY_CONFIRM,
    banner: { tone: 'red', text },
    result: 'Clay broke on a network error before finishing',
    checks: clayChecks(ctx, {
      emailValidation: NOT_REPORTED,
      copyQa: NOT_REPORTED,
      delivery: { state: 'error', reason: 'Clay broke on a network error' },
    }),
  });
}

function capitalize(text: string): string {
  return text.charAt(0).toUpperCase() + text.slice(1);
}

/**
 * The note on a row Terra repaired to delivered after a network-error crash,
 * because Gong showed the person already in the flow (o) — so a rep who
 * remembers a red row sees why it is green and that nothing went twice.
 */
function repairedFromCrashBanner(ctx: Ctx): { tone: 'grey'; text: string } | null {
  if (readCrashTriage(ctx.row.result_data)?.outcome !== 'repaired_enrolled') return null;
  const who = firstName(ctx.row) ?? 'this person';
  return {
    tone: 'grey',
    text: `Clay broke on a network error, but Gong shows ${who} in the flow — so Terra marked this delivered and didn't resend.`,
  };
}

/**
 * "Retrying after a network error in Clay · try 2 of 3" while one of Terra's
 * OWN retries (o) is on its way — `null` for every other in-flight row.
 */
function autoRetryPill(ctx: Ctx): string | null {
  if (readCrashTriage(ctx.row.result_data)?.outcome !== 'requeued') return null;
  const attempt = readAutoRetries(ctx.row.result_data).at(-1)?.attempt;
  if (attempt === undefined) return null;
  return `Retrying after a network error in Clay · try ${String(attempt)} of ${String(TRANSPORT_CRASH_AUTO_RETRY_MAX)}`;
}

function terminal(ctx: Ctx, clay: ClayRunResultData, resultStatus: string): RepStatusView {
  const word = clay.clay_run.status;
  const blockReason = plainBlockReason(clay.clay_block?.reason);

  // Clay finished the run but its outcome is gone (written by the re-check) —
  // before the "Added to" check and the green fallback, which would guess.
  if (word === CLAY_RUN_STATUS_FINISHED_UNTRACKED) return notTracked(ctx);

  // Delivered — Clay's own "Added to …" word, verbatim. A row Terra repaired
  // after a network-error crash (o) says why.
  if (/^Added to /.test(word) && resultStatus !== 'clay_failed') {
    const repaired = repairedFromCrashBanner(ctx);
    const deliveredView = delivered(ctx, word);
    return repaired === null ? deliveredView : { ...deliveredView, banner: repaired };
  }

  // A network error inside Clay (o) — before the generic crash below, which
  // is never retryable.
  if (isTransportCrash(ctx.row)) {
    return transportCrashed(ctx, clay.clay_alert_posted === true || clay.clay_crash_alerted === true);
  }

  if (word === 'Test preview' || word === 'test_preview') {
    return view(ctx, {
      status: 'delivered',
      pillText: 'Test run — nothing was sent',
      banner: { tone: 'grey', text: 'Test run — Clay built this copy and sent nothing.' },
      result: 'Test run — nothing was sent',
      checks: clayChecks(ctx, {
        emailValidation: PASSED,
        copyQa: PASSED,
        delivery: { state: 'skip', reason: 'Test run' },
      }),
    });
  }

  // Not sent — a deliberate decision, whatever `result_status` the row was stored with.
  if (isNonSendWord(word)) {
    const why = blockReason ?? NOT_SENT_PILL[word];
    const emailStop = word === 'Invalid Email' || word === 'Suppressed';
    return view(ctx, {
      status: 'not_sent',
      pillText: NOT_SENT_PILL[word],
      banner: notSentBanner(ctx),
      result: NOT_SENT_RESULT[word],
      checks: clayChecks(ctx, {
        emailValidation: emailStop ? { state: 'stop', reason: why } : PASSED,
        copyQa: emailStop ? NOT_REACHED : PASSED,
        delivery: emailStop ? NOT_REACHED : { state: 'stop', reason: why },
      }),
    });
  }

  if (resultStatus === 'clay_blocked') return clayStopped(ctx, blockReason ?? 'Clay gave no reason');

  // Clay REFUSED the send — its own final-status decision, with a reason only a
  // rep or an admin can act on. A stop, never a crash, and ops is not alerted.
  // A routine item that genuinely threw carries `node: 'Clay routine run'` and
  // falls through to `crashed()` below, as does an `Error` whose reason cleans
  // to nothing. [R16][R20], edge case (l).
  //
  // (n): only a reason the `Stop unsafe delivery` gate produced proves nothing
  // was sent. `Gong returned no enrollment id` reaches this same branch and
  // does NOT — it is non-retryable precisely because delivery may already have
  // happened — so its banner hedges instead of guaranteeing non-delivery.
  if (word === 'Error' && clay.clay_block?.node === FINAL_STATUS_BLOCK_NODE) {
    const described = describeBlockReason(clay.clay_block.reason);
    if (described !== null) {
      const copyQa = described.findings !== null || /copy qa/i.test(described.headline);
      const provenSafe = isStopUnsafeDeliveryReason(clay.clay_block.reason);
      return clayStopped(ctx, described.headline, { copyQa, findings: described.findings }, provenSafe);
    }
  }

  // Crashed — `Error`, a routine item that failed, or any clay_failed we don't recognise.
  if (resultStatus === 'clay_failed' || word === 'Error' || word === 'failed') {
    return crashed(ctx, clay.clay_alert_posted === true || clay.clay_crash_alerted === true);
  }

  // Completed with a word we don't specifically know (legacy `delivered` / `complete`, …).
  return delivered(ctx, ctx.channelLabel === null ? 'Delivered' : `Added to ${ctx.channelLabel}`);
}

function terraSkip(ctx: Ctx, row: PlaybooksDispatchLogRow): RepStatusView {
  const copy = isCopyDefectError(row.error);
  const pill = copy
    ? "Copy had a merge field Terra couldn't fill"
    : isSenderMissingError(row.error)
      ? 'No sender could be found for this person'
      : isMemberRemovedError(row.error)
        ? 'No longer in the campaign'
        : "Terra couldn't prepare this message";
  const detail = skippedMemberSummary(row.error ?? '');
  const name = firstName(row);
  return view(ctx, {
    status: 'not_sent',
    pillText: pill,
    banner: {
      tone: 'grey',
      text: `Nothing went out${name === null ? '' : ` to ${name}`}. Terra held this person back${detail === '' ? '.' : `: ${detail}`}`,
    },
    result: pill,
    checks: {
      qualification: ctx.qualification,
      emailValidation: NOT_REACHED,
      copyQa: copy ? { state: 'stop', reason: pill } : NOT_REACHED,
      delivery: copy ? NOT_REACHED : { state: 'stop', reason: pill },
    },
  });
}

function failedToStart(ctx: Ctx, row: PlaybooksDispatchLogRow): RepStatusView {
  const retryable = isRetryableDispatchRow(row);
  // U0e item 1: Clay turned the START away because it was busy (429). A
  // refused admission created no run, so Retry's check finds none and sends.
  if (isClayBusyStartFailure(row)) {
    return view(ctx, {
      status: 'failed_to_start',
      pillText: 'Failed to start',
      retryable,
      banner: {
        tone: 'red',
        text: `Clay was too busy to take this send, so nothing went out. Retry sends it to ${firstName(row) ?? 'this person'} now.`,
      },
      result: 'Clay was too busy to start this send.',
      checks: {
        qualification: ctx.qualification,
        emailValidation: NOT_REACHED,
        copyQa: NOT_REACHED,
        delivery: NOT_REACHED,
      },
    });
  }
  const reason =
    dispatchFailureSummary(row)
    ?? (row.error === null ? null : dispatchStartFailureSummary(row.error))
    ?? DISPATCH_START_FAILURE_BASE;
  return view(ctx, {
    status: 'failed_to_start',
    pillText: 'Failed to start',
    retryable,
    banner: {
      tone: 'red',
      text: retryable
        ? 'Nothing went out yet. This is the copy that will be sent when you retry.'
        : 'Nothing went out.',
    },
    result: reason,
    checks: {
      qualification: ctx.qualification,
      emailValidation: NOT_REACHED,
      copyQa: NOT_REACHED,
      delivery: NOT_REACHED,
    },
  });
}

/**
 * One row → the rep's view. `nowMs` is passed in (never read here) so the
 * young/stale split is deterministic in tests.
 */
export function classifyRepStatus(row: PlaybooksDispatchLogRow, nowMs: number): RepStatusView {
  const clay = readClayRunResultData(row.result_data);
  const ctx: Ctx = {
    row,
    clay,
    channelLabel: repChannelLabel(row.channel),
    qualification: qualificationCheck(row),
  };
  const category = classifyDispatchResult(row);

  // 1–2. Failed rows: a deliberate Terra-side skip, or a START that never reached Clay.
  if (category === 'failed') {
    if (isSkippedMemberError(row.error)) return terraSkip(ctx, row);
    return failedToStart(ctx, row);
  }

  // 3. Terra hasn't finished handing it to Clay.
  if (category === 'queued' || category === 'processing' || category === 'other') {
    const reconciling = row.result === 'processing' && readDispatchReconcile(row.result_data) !== null;
    const retrying = autoRetryPill(ctx);
    return inProgress(ctx, reconciling ? DISPATCH_RECONCILING_CAPTION : (retrying ?? 'Starting the send…'), {
      bannerText: 'Terra is handing this to Clay now.',
    });
  }

  const resultStatus = row.result_status;

  // 5–6. Clay has the run and hasn't finished it (5), or the poll gave up on it
  // (6, `clay_not_found` — never red). Stale iff the SHARED re-check rule says
  // so (`dispatch-recheck-eligibility.ts`), so the stale pill and the auto
  // re-check never ask for something the server then refuses.
  if (resultStatus === 'clay_not_found' || resultStatus === 'clay_pending' || resultStatus === 'clay_running') {
    const lastWordAt = Date.parse(rowStatusAnchor(row) ?? '');
    const ageMs = Number.isNaN(lastWordAt) ? null : nowMs - lastWordAt;
    if (isRecheckEligible(row, ageMs)) {
      return inProgress(ctx, STALE_PILL, { needsRecheck: true, bannerText: STALE_BANNER });
    }
    const s = sending(ctx);
    return inProgress(ctx, autoRetryPill(ctx) ?? s.pill, { bannerText: s.banner });
  }

  // 7a. A terminal `result_status` whose `result_data` Terra can't read —
  // decide by the status alone rather than falling through to row 4 (which
  // would show a crash as "In progress", then green).
  if (
    clay === null
    && (resultStatus === 'clay_failed' || resultStatus === 'clay_blocked' || resultStatus === 'clay_completed')
  ) {
    if (resultStatus === 'clay_failed') return crashed(ctx, false);
    if (resultStatus === 'clay_blocked') return clayStopped(ctx, 'Clay gave no reason');
    return notTracked(ctx);
  }

  // 7. A terminal Clay outcome — by Clay's own word first.
  if (
    clay !== null
    && (resultStatus === 'clay_completed' || resultStatus === 'clay_blocked' || resultStatus === 'clay_failed')
  ) {
    return terminal(ctx, clay, resultStatus);
  }

  // 4. Sent, and Clay has said nothing Terra can read (webhook transport, or pre-feature).
  // Aged from when the row actually STARTED (plan 2026-10-14-002 U0d), not when it was written.
  if (isRecent(rowStatusAnchor(row), nowMs, CLAY_STATUS_WAIT_MS)) {
    const s = sending(ctx);
    return inProgress(ctx, s.pill, { bannerText: s.banner });
  }
  return notTracked(ctx);
}

export type RepStatusCounts = Record<RepDispatchStatus, number>;

export function countByRepStatus(views: readonly Pick<RepStatusView, 'status'>[]): RepStatusCounts {
  const counts: RepStatusCounts = { delivered: 0, in_progress: 0, not_sent: 0, failed_to_start: 0, crashed: 0 };
  for (const v of views) counts[v.status] += 1;
  return counts;
}

/** `12 people · 10 delivered · 2 not sent` — zero states omitted, prototype order. */
export function summaryLine(counts: RepStatusCounts): string {
  const total = REP_STATUS_ORDER.reduce((n, status) => n + counts[status], 0);
  const parts = [`${String(total)} ${total === 1 ? 'person' : 'people'}`];
  for (const status of REP_STATUS_ORDER) {
    if (counts[status] > 0) parts.push(repStatusCountLabel(status, counts[status]));
  }
  return parts.join(' · ');
}

/**
 * The rows the inspector re-asks Clay about when a send card mounts (plan
 * 2026-09-27-003, U4): exactly the stale in-progress ones — `clay_not_found`,
 * and `clay_pending` / `clay_running` past the window the poll watches. The
 * server applies the SAME rule (`dispatch-recheck-eligibility.ts#isRecheckEligible`).
 */
export function rowsNeedingRecheck<T extends PlaybooksDispatchLogRow>(rows: readonly T[], nowMs: number): T[] {
  return rows.filter((row) => classifyRepStatus(row, nowMs).needsRecheck);
}
